import React, { useState, useEffect } from 'react';
import { Search, Bot, Loader2, AlertCircle, FileText, Database, RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';

interface SearchResult {
  id: string;
  query: string;
  search_params: {
    results: Array<{
      title: string;
      url: string;
      content: string;
    }>;
  };
  extraido: boolean;
  asistente_id: string;
  created_at: string;
}

interface ExtractResult {
  id: string;
  url: string;
  texto_extraido: string | null;
  chunk: boolean;
  embedding: boolean;
  vectorizado: boolean;
  error?: string;
}

interface ExtractConfig {
  id: string;
  nombre: string;
  auth_token: string;
  endpoint: string;
  include_images: boolean;
  extract_depth: 'basic' | 'advanced';
}

interface ExtractResponse {
  results: Array<{
    url: string;
    raw_content: string;
    images: any[];
  }>;
  failed_results: Array<{
    url: string;
    error: string;
  }>;
  response_time: number;
}

export default function TavilySearchResults() {
  const { user } = useAuthStore();
  const [results, setResults] = useState<SearchResult[]>([]);
  const [extractResults, setExtractResults] = useState<ExtractResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showProcessed, setShowProcessed] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<string>('');
  const [configs, setConfigs] = useState<ExtractConfig[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchResults();
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const { data, error } = await supabase
        .from('tavily_extract_configs')
        .select('*');

      if (error) throw error;
      setConfigs(data || []);
      if (data && data.length > 0) {
        setSelectedConfig(data[0].id);
      }
    } catch (error: any) {
      console.error('Error fetching configs:', error);
      setError('Error al cargar las configuraciones');
    }
  };

  const fetchResults = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Fetch search results
      const { data: searchResults, error: searchError } = await supabase
        .from('tavily_search_results')
        .select('*')
        .order('created_at', { ascending: false });

      if (searchError) throw searchError;

      // Fetch extract results
      const { data: extractData, error: extractError } = await supabase
        .from('tavily_extract_results')
        .select('*');

      if (extractError) throw extractError;

      setResults(searchResults || []);
      setExtractResults(extractData || []);
    } catch (error: any) {
      console.error('Error fetching results:', error);
      setError('Error al cargar los resultados');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExtractText = async () => {
    if (!selectedConfig) {
      setError('Por favor seleccione una configuración de extracción');
      return;
    }

    try {
      setIsProcessing(true);
      setError('');
      setSuccess('');

      const config = configs.find(c => c.id === selectedConfig);
      if (!config) throw new Error('Configuración no encontrada');

      for (const result of results) {
        if (result.extraido) continue;

        for (const searchResult of result.search_params.results) {
          try {
            // Skip known problematic URLs
            if (
              searchResult.url.includes('youtube.com') || 
              searchResult.url.includes('facebook.com') ||
              searchResult.url.includes('twitter.com') ||
              searchResult.url.includes('instagram.com')
            ) {
              console.log(`Skipping social media URL: ${searchResult.url}`);
              continue;
            }

            // Validate URL
            try {
              const url = new URL(searchResult.url);
              if (!url.protocol.startsWith('http')) {
                throw new Error('URL inválida');
              }
            } catch (urlError) {
              console.error(`Invalid URL: ${searchResult.url}`);
              continue;
            }

            // Clean up auth token
            const authToken = config.auth_token.replace('Bearer ', '').trim();
            if (!authToken.startsWith('tvly-')) {
              console.warn('El token no tiene el prefijo tvly-');
            }

            console.log(`Procesando URL: ${searchResult.url}`);

            // Extract text using Tavily API with correct format
            const response = await fetch(config.endpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`
              },
              body: JSON.stringify({
                urls: [searchResult.url], // Array with single URL
                include_images: config.include_images,
                extract_depth: config.extract_depth
              })
            });

            console.log(`Status code: ${response.status}`);

            const responseData: ExtractResponse = await response.json();
            console.log('API Response:', responseData);

            let errorMessage = '';
            let extractedText = null;

            if (!response.ok) {
              errorMessage = responseData.failed_results?.[0]?.error || 
                           `Error ${response.status}: ${response.statusText}`;
              
              if (response.status === 422) {
                errorMessage = 'Error de validación en la API: URL no accesible o formato inválido';
              } else if (response.status === 401) {
                errorMessage = 'Error de autenticación: Token inválido o expirado';
              } else if (response.status === 429) {
                errorMessage = 'Se ha excedido el límite de la API';
              }

              console.error(`Error for URL ${searchResult.url}:`, errorMessage);
            } else {
              // Check for failed results first
              if (responseData.failed_results?.length > 0) {
                errorMessage = responseData.failed_results[0].error || 'Error al extraer contenido';
                console.error(`Failed to extract from URL ${searchResult.url}:`, errorMessage);
              } else {
                // Get text from raw_content in results array
                extractedText = responseData.results?.[0]?.raw_content;
                
                if (!extractedText) {
                  errorMessage = 'No se pudo extraer texto de la URL';
                  console.error('No text in response:', responseData);
                } else {
                  console.log(`Texto extraído (${extractedText.length} caracteres)`);
                }
              }
            }

            // Save result (success or error)
            const { error: insertError } = await supabase
              .from('tavily_extract_results')
              .insert({
                url: searchResult.url,
                texto_extraido: extractedText,
                asistente_id: result.asistente_id,
                usuario_id: user?.id,
                config_id: config.id,
                chunk: false,
                embedding: false,
                vectorizado: false,
                error: errorMessage || null
              });

            if (insertError) {
              console.error('Error saving to database:', insertError);
              throw insertError;
            }

            // Mark search result as processed even if there was an error
            const { error: updateError } = await supabase
              .from('tavily_search_results')
              .update({ extraido: true })
              .eq('id', result.id);

            if (updateError) {
              console.error('Error updating search result:', updateError);
            }

            // Add a small delay between requests to avoid rate limiting
            await new Promise(resolve => setTimeout(resolve, 1000));

          } catch (error: any) {
            console.error(`Error processing URL ${searchResult.url}:`, error);
            
            // Save error information
            await supabase
              .from('tavily_extract_results')
              .insert({
                url: searchResult.url,
                error: error.message || 'Error desconocido al extraer texto',
                asistente_id: result.asistente_id,
                usuario_id: user?.id,
                config_id: config.id
              });
          }
        }
      }

      setSuccess('Extracción de textos completada');
      fetchResults();
    } catch (error: any) {
      console.error('Error in text extraction:', error);
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const filteredResults = results.filter(result => {
    if (!showProcessed && result.extraido) {
      return false;
    }

    if (searchTerm) {
      return (
        result.query.toLowerCase().includes(searchTerm.toLowerCase()) ||
        result.search_params.results.some(r => 
          r.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.url.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    return true;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Resultados de Búsqueda Tavily</h1>
        <div className="flex items-center space-x-4">
          <select
            value={selectedConfig}
            onChange={(e) => setSelectedConfig(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md"
          >
            {configs.map(config => (
              <option key={config.id} value={config.id}>
                {config.nombre}
              </option>
            ))}
          </select>
          <button
            onClick={handleExtractText}
            disabled={isProcessing}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center space-x-2"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Procesando...</span>
              </>
            ) : (
              <>
                <FileText className="w-5 h-5" />
                <span>Extraer Textos</span>
              </>
            )}
          </button>
          <button
            onClick={fetchResults}
            disabled={isProcessing}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
            title="Actualizar resultados"
          >
            <RefreshCw className={`w-5 h-5 ${isProcessing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={showProcessed}
                onChange={(e) => setShowProcessed(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>Mostrar procesados</span>
            </label>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span>Chunk</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span>Embedding</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
              <span>Vectorizado</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {filteredResults.map((result) => (
            <div key={result.id} className="border rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-semibold text-lg text-gray-800">
                    {result.query}
                  </h3>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 text-sm rounded-full ${
                    result.extraido
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {result.extraido ? 'Procesado' : 'Pendiente'}
                  </span>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                {result.search_params.results.map((searchResult, index) => {
                  const extractResult = extractResults.find(er => er.url === searchResult.url);
                  return (
                    <div key={index} className="border-l-4 border-blue-200 pl-4 py-2">
                      <div className="flex items-center justify-between">
                        <a
                          href={searchResult.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {searchResult.title}
                        </a>
                        <div className="flex items-center space-x-2">
                          <div className={`w-3 h-3 rounded-full ${
                            extractResult?.chunk ? 'bg-green-500' : 'bg-gray-300'
                          }`}></div>
                          <div className={`w-3 h-3 rounded-full ${
                            extractResult?.embedding ? 'bg-blue-500' : 'bg-gray-300'
                          }`}></div>
                          <div className={`w-3 h-3 rounded-full ${
                            extractResult?.vectorizado ? 'bg-purple-500' : 'bg-gray-300'
                          }`}></div>
                        </div>
                      </div>
                      {extractResult?.error && (
                        <div className="text-red-600 text-sm mt-1">
                          Error: {extractResult.error}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {filteredResults.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No se encontraron resultados</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}